import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { supabase, supabasePublic } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { validateBarSubmission } from '../utils/barValidation';
import { prepareFileForUpload } from '../lib/fileUtils';
import { validateContent } from '../lib/contentFilter';
import { trackEvent } from '../lib/posthog';
import { notifyNewFollower, notifyBarComment, notifyFriendRating, notifyFriendRequest, notifyRatingComment, notifyCheckIn } from '../lib/pushNotifications';
import { getMetroById } from '../data/metros';
import { inferBarCity } from '../utils/geo';
import { getEnabledMetros } from '../data/metros';
import {
  getBroaderMetroCoreName,
  isBroaderMetroNeighborhood,
  municipalityFromNominatim,
  parseTownFromAddress,
  reverseGeocodeTown,
} from '../utils/barLocation';
import { canCheckInAtBar, formatCheckInMessage, pickCheckInPhrase } from '../data/checkInPhrases';

const AppContext = createContext(null);

const SESSION_KEYS = {
  INITIAL_LOAD_COMPLETE: 'tilted_initial_load_complete',
  LAST_FETCH: 'tilted_last_fetch',
  BARS: 'tilted_cache_bars_v3',
  RATINGS: 'tilted_cache_ratings',
  USERS: 'tilted_cache_users',
  COMMENTS: 'tilted_cache_comments',
  FOLLOWING: 'tilted_cache_following',
  FOLLOWERS: 'tilted_cache_followers',
};

const STALE_THRESHOLD = 5 * 60 * 1000;

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeCache(key, data) {
  try { sessionStorage.setItem(key, JSON.stringify(data)); } catch {}
}

const BARS_PAGE_SIZE = 1000;

async function fetchAllBars() {
  const withMetro = 'id, name, address, neighborhood, description, lat, lng, created_at, submitted_by, submitted_at, auto_approved, attributes, metro_id, city, state';
  const fallback = 'id, name, address, neighborhood, description, lat, lng, created_at, submitted_by, submitted_at, auto_approved, attributes';

  const load = async (columns) => {
    const rows = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabasePublic
        .from('bars')
        .select(columns)
        .order('name')
        .range(from, from + BARS_PAGE_SIZE - 1);
      if (error) return { data: rows, error };
      if (!data?.length) return { data: rows, error: null };
      rows.push(...data);
      if (data.length < BARS_PAGE_SIZE) return { data: rows, error: null };
      from += BARS_PAGE_SIZE;
    }
  };

  const first = await load(withMetro);
  if (first.error && /metro_id|city|state/i.test(first.error.message || '')) {
    return load(fallback);
  }
  return first;
}

export function AppProvider({ children }) {
  const { user, profile, logActivity } = useAuth();
  
  const [bars, setBars] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [friends, setFriends] = useState([]); // Legacy - people you're following
  const [friendRequests, setFriendRequests] = useState({ incoming: [], outgoing: [] }); // Legacy
  // New follow system
  const [following, setFollowing] = useState([]); // People you follow
  const [followers, setFollowers] = useState([]); // People who follow you
  const [followRequests, setFollowRequests] = useState({ incoming: [], outgoing: [] }); // Pending follow requests
  const [notifications, setNotifications] = useState([]);
  const [lists, setLists] = useState([]);
  const [friendLists, setFriendLists] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false); // For background refresh indicator
  
  const isInitialLoadRef = useRef(!sessionStorage.getItem(SESSION_KEYS.INITIAL_LOAD_COMPLETE));
  const lastFetchTimeRef = useRef(null);
  const fetchInProgressRef = useRef(false);
  const fetchUserInProgressRef = useRef(false);
  const cacheHydratedRef = useRef(false);
  const realtimeDebounceRef = useRef(null);

  // Hydrate from cache on first render so the UI is instant
  useEffect(() => {
    if (cacheHydratedRef.current) return;
    cacheHydratedRef.current = true;
    const cachedBars = readCache(SESSION_KEYS.BARS);
    const cachedRatings = readCache(SESSION_KEYS.RATINGS);
    const cachedUsers = readCache(SESSION_KEYS.USERS);
    const cachedComments = readCache(SESSION_KEYS.COMMENTS);
    if (cachedBars?.length) {
      const metros = getEnabledMetros();
      const hydratedBars = cachedBars.map((bar) => {
        const inferred = inferBarCity({
          ...bar,
          lat: Number(bar.lat),
          lng: Number(bar.lng),
          metroId: bar.metroId || bar.metro_id || null,
        }, metros);
        return { ...bar, lat: Number(bar.lat), lng: Number(bar.lng), ...inferred };
      });
      console.log('Cache hydrated:', hydratedBars.length, 'bars');
      setBars(hydratedBars);
      if (cachedRatings?.length) setRatings(cachedRatings);
      if (cachedUsers?.length) setUsers(cachedUsers);
      if (cachedComments?.length) setComments(cachedComments);
      setLoading(false);
    }
    // Load followers/following from localStorage (survives app close)
    try {
      const lsFollowing = localStorage.getItem('tilted_ls_following');
      const lsFollowers = localStorage.getItem('tilted_ls_followers');
      if (lsFollowing) setFollowing(JSON.parse(lsFollowing));
      if (lsFollowers) setFollowers(JSON.parse(lsFollowers));
    } catch { /* ignore */ }
  }, []);

  // Fetch core data from Supabase (bars, ratings, comments, profiles, photos)
  // background: if true, don't show loading screen (for tab switch/real-time updates)
  const fetchData = useCallback(async (background = false) => {
    // Prevent concurrent fetches and rapid re-fetches
    if (fetchInProgressRef.current) {
      console.log('fetchData: already in progress, skipping');
      return;
    }
    const now = Date.now();
    if (lastFetchTimeRef.current && now - lastFetchTimeRef.current < 10000) {
      console.log('fetchData: fetched <10s ago, skipping');
      return;
    }
    fetchInProgressRef.current = true;

    console.log('fetchData: starting core data fetch, background:', background);

    const hasCachedData = !!sessionStorage.getItem(SESSION_KEYS.BARS);
    if (!background && isInitialLoadRef.current && !hasCachedData) {
      setLoading(true);
    } else if (background || hasCachedData) {
      setIsRefreshing(true);
    }
    setError(null);

    const forceTimeout = setTimeout(() => {
      console.warn('Data fetch timeout - forcing complete with partial data');
      fetchInProgressRef.current = false;
      setLoading(false);
      setIsRefreshing(false);
    }, 15000);

    try {
      console.log('Fetching core data in parallel (public client)...');
      const coreQueries = [
        fetchAllBars(),
        supabasePublic.from('ratings').select('*').order('created_at', { ascending: false }),
        supabasePublic.from('comments').select('*').order('created_at', { ascending: false }),
        supabasePublic.from('profiles').select('*').order('display_name'),
        supabasePublic.from('bar_photos').select('*').order('created_at', { ascending: false })
      ];

      const allResults = await Promise.allSettled(coreQueries);

      const getResult = (idx, name) => {
        const r = allResults[idx];
        if (r?.status !== 'fulfilled') {
          console.error(`${name} query rejected:`, r?.reason);
          return null;
        }
        if (r.value.error) {
          console.error(`${name} query error:`, r.value.error.message);
          return null;
        }
        return r.value.data;
      };

      const barsPayload = allResults[0]?.status === 'fulfilled' ? allResults[0].value : null;
      let barsData = barsPayload?.data || null;
      if (barsPayload?.error && !barsData?.length) {
        console.error('bars query error:', barsPayload.error.message);
        barsData = null;
      }
      const ratingsData = getResult(1, 'ratings');
      const commentsData = getResult(2, 'comments');
      const usersData = getResult(3, 'profiles');
      const photosData = getResult(4, 'photos');

      console.log('Core data:', barsData?.length, 'bars,', ratingsData?.length, 'ratings,', usersData?.length, 'users');

      const enabledMetros = getEnabledMetros();

      // Transform data to match existing format
      const transformedBars = (barsData || []).map(bar => {
        // Safety net: strip any non-primitive values to prevent React #310 crashes
        const safe = {};
        for (const [key, val] of Object.entries(bar)) {
          if (val === null || val === undefined || typeof val !== 'object') {
            safe[key] = val;
          }
        }
        safe.attributes = bar.attributes || {};
        safe.lat = Number(bar.lat);
        safe.lng = Number(bar.lng);
        const inferred = inferBarCity({
          ...safe,
          metroId: bar.metro_id || bar.metroId || null,
          city: bar.city || null,
          state: bar.state || null,
        }, enabledMetros);
        safe.metroId = inferred.metroId;
        safe.city = inferred.city;
        safe.state = inferred.state;
        return safe;
      });

      const transformedRatings = (ratingsData || []).map(r => {
        const ratingUser = (usersData || []).find(u => u.id === r.user_id);
        return {
          id: r.id,
          barId: r.bar_id,
          userId: r.user_id,
          userName: ratingUser?.display_name || ratingUser?.username || 'Anonymous',
          overall: parseFloat(r.overall),
          price: r.price ? parseFloat(r.price) : null,
          ambiance: r.ambiance ? parseFloat(r.ambiance) : null,
          crowd: r.crowd ? parseFloat(r.crowd) : null,
          service: r.service ? parseFloat(r.service) : null,
          drinkSelection: r.drink_selection ? parseFloat(r.drink_selection) : null,
          isDiveBar: r.is_dive_bar,
          createdAt: new Date(r.created_at).getTime()
        };
      });

      const transformedComments = (commentsData || []).map(c => {
        const commentUser = (usersData || []).find(u => u.id === c.user_id);
        return {
          id: c.id,
          barId: c.bar_id,
          userId: c.user_id,
          userName: commentUser?.display_name || commentUser?.username || 'Anonymous',
          userUsername: commentUser?.username,
          userAvatar: commentUser?.avatar_url,
          text: c.text,
          visibility: c.visibility || 'public',
          isReview: c.is_review || false,
          createdAt: new Date(c.created_at).getTime()
        };
      });

      const transformedUsers = (usersData || []).map(u => ({
        id: u.id,
        username: u.username,
        displayName: u.display_name,
        avatarUrl: u.avatar_url,
        isAdmin: u.is_admin,
        joinedAt: new Date(u.created_at).getTime()
      }));

      const transformedPhotos = (photosData || []).map(p => {
        const photoUser = (usersData || []).find(u => u.id === p.user_id);
        return {
          id: p.id,
          barId: p.bar_id,
          userId: p.user_id,
          userName: photoUser?.display_name || photoUser?.username || 'Anonymous',
          storagePath: p.storage_path,
          caption: p.caption,
          createdAt: new Date(p.created_at).getTime(),
          url: supabase.storage.from('bar-photos').getPublicUrl(p.storage_path).data.publicUrl
        };
      });

      console.log('Data ready:', transformedBars.length, 'bars,', transformedRatings.length, 'ratings,', transformedUsers.length, 'users');

      // Only update state + cache if we got real data — never overwrite good data with empty
      if (transformedBars.length > 0) {
        setBars(transformedBars);
        writeCache(SESSION_KEYS.BARS, transformedBars);
      }
      if (transformedRatings.length > 0) {
        setRatings(transformedRatings);
        writeCache(SESSION_KEYS.RATINGS, transformedRatings);
      }
      if (transformedUsers.length > 0) {
        setUsers(transformedUsers);
        writeCache(SESSION_KEYS.USERS, transformedUsers);
      }
      if (transformedComments.length > 0) {
        setComments(transformedComments);
        writeCache(SESSION_KEYS.COMMENTS, transformedComments);
      }
      if (transformedPhotos.length > 0) {
        setPhotos(transformedPhotos);
      }

      // Auto-retry once if bars failed on initial load
      if (transformedBars.length === 0 && isInitialLoadRef.current) {
        console.warn('Initial fetch returned 0 bars — scheduling retry in 3s');
        setTimeout(() => {
          lastFetchTimeRef.current = null;
          fetchInProgressRef.current = false;
          fetchData(false);
        }, 3000);
      }
    } catch (err) {
      console.error('fetchData error:', err.message);
      setError(err.message || 'Failed to load data. Please refresh.');

      // Auto-retry on error during initial load
      if (isInitialLoadRef.current) {
        console.warn('Initial fetch errored — scheduling retry in 3s');
        setTimeout(() => {
          lastFetchTimeRef.current = null;
          fetchInProgressRef.current = false;
          fetchData(false);
        }, 3000);
      }
    } finally {
      clearTimeout(forceTimeout);
      fetchInProgressRef.current = false;
      setLoading(false);
      setIsRefreshing(false);

      if (isInitialLoadRef.current) {
        sessionStorage.setItem(SESSION_KEYS.INITIAL_LOAD_COMPLETE, 'true');
        isInitialLoadRef.current = false;
      }
      sessionStorage.setItem(SESSION_KEYS.LAST_FETCH, Date.now().toString());
      lastFetchTimeRef.current = Date.now();

      console.log('fetchData: complete');
    }
  }, []);

  // Fetch user-specific data (following, followers, friends, notifications, lists)
  // Separate from fetchData so it fires immediately when user becomes available
  // without being blocked by the core data debounce guard
  const fetchUserData = useCallback(async () => {
    if (!user) return;
    if (fetchUserInProgressRef.current) {
      console.log('fetchUserData: already in progress, skipping');
      return;
    }
    fetchUserInProgressRef.current = true;

    console.log('fetchUserData: starting for user:', user.id);

    try {
      const userQueries = [
        supabase.from('friendships').select('friend_id').eq('user_id', user.id),
        supabase.from('follows').select('following_id, status').eq('follower_id', user.id),
        supabase.from('follows').select('follower_id, status, created_at, profiles:follower_id (id, username, display_name, avatar_url)').eq('following_id', user.id),
        supabase.from('friend_requests').select('*, from_user:from_user_id (id, username, display_name, avatar_url)').eq('to_user_id', user.id).eq('status', 'pending').order('created_at', { ascending: false }),
        supabase.from('friend_requests').select('*, to_user:to_user_id (id, username, display_name, avatar_url)').eq('from_user_id', user.id).eq('status', 'pending').order('created_at', { ascending: false }),
        supabase.from('notifications').select('id, user_id, type, from_user_id, reference_id, data, is_read, created_at, from_user:from_user_id (id, username, display_name, avatar_url)').eq('user_id', user.id).order('created_at', { ascending: false }).limit(50),
        supabase.from('lists').select('*, list_bars(id, bar_id, position, added_at)').eq('user_id', user.id).order('created_at', { ascending: false })
      ];

      const allResults = await Promise.allSettled(userQueries);
      const getResult = (idx) => allResults[idx]?.status === 'fulfilled' ? allResults[idx].value.data : null;

      const friendshipsResult = getResult(0);
      const followingResult = getResult(1);
      const followersResult = getResult(2);
      const incomingResult = getResult(3);
      const outgoingResult = getResult(4);
      const notifResult = getResult(5);
      const listsResult = getResult(6);

      let friendsData = [];

      // Process friendships
      if (friendshipsResult) {
        friendsData = friendshipsResult.map(f => f.friend_id);
      }

      // Process follows
      if (followingResult) {
        const activeFollowing = followingResult.filter(f => f.status === 'active').map(f => f.following_id);
        setFollowing(activeFollowing);
        writeCache(SESSION_KEYS.FOLLOWING, activeFollowing);
        try { localStorage.setItem('tilted_ls_following', JSON.stringify(activeFollowing)); } catch {}
        friendsData = [...new Set([...friendsData, ...activeFollowing])];
        const pendingOutgoing = followingResult.filter(f => f.status === 'pending');
        setFollowRequests(prev => ({ ...prev, outgoing: pendingOutgoing }));
      }

      // Process followers
      if (followersResult) {
        const activeFollowers = followersResult.filter(f => f.status === 'active').map(f => f.follower_id);
        setFollowers(activeFollowers);
        writeCache(SESSION_KEYS.FOLLOWERS, activeFollowers);
        try { localStorage.setItem('tilted_ls_followers', JSON.stringify(activeFollowers)); } catch {}
        const pendingIncoming = followersResult.filter(f => f.status === 'pending').map(f => ({
          id: `${f.follower_id}-${user.id}`,
          follower_id: f.follower_id,
          created_at: f.created_at,
          from_user: f.profiles
        }));
        setFollowRequests(prev => ({ ...prev, incoming: pendingIncoming }));
      }

      // Process friend requests
      const incomingRequests = incomingResult || [];
      const outgoingRequests = outgoingResult || [];
      setFriendRequests({ incoming: incomingRequests, outgoing: outgoingRequests });

      // Process notifications
      setNotifications(notifResult || []);

      // Process lists
      setLists(listsResult || []);

      setFriends(friendsData);

      // Friend lists — fetch in background, don't block
      if (friendsData.length > 0) {
        supabase
          .from('lists')
          .select('*, list_bars(id, bar_id, position, added_at), profiles:user_id (id, username, display_name, avatar_url)')
          .in('user_id', friendsData)
          .in('privacy', ['public', 'friends'])
          .eq('is_system', false)
          .order('created_at', { ascending: false })
          .then(({ data: friendListsResult }) => {
            if (friendListsResult) setFriendLists(friendListsResult);
          });
      }

      console.log('fetchUserData: complete');
    } catch (err) {
      console.error('fetchUserData error:', err.message);
    } finally {
      fetchUserInProgressRef.current = false;
    }
  }, [user]);

  // Initial core data load (fires once on mount)
  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  // Fetch user-specific data when user becomes available
  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [fetchUserData]);

  // Clear user-specific state on sign-out
  useEffect(() => {
    if (!user) {
      setFollowing([]);
      setFollowers([]);
      setFollowRequests({ incoming: [], outgoing: [] });
      setFriends([]);
      setFriendRequests({ incoming: [], outgoing: [] });
      setNotifications([]);
      setLists([]);
      setFriendLists([]);
      // Clear user-specific caches
      sessionStorage.removeItem(SESSION_KEYS.FOLLOWING);
      sessionStorage.removeItem(SESSION_KEYS.FOLLOWERS);
      // Reset debounce so next login fetches immediately
      lastFetchTimeRef.current = null;
    }
  }, [user]);

  // Page/app visibility change handler - refresh data in background when app becomes visible
  useEffect(() => {
    const refreshIfStale = () => {
      const lastFetch = sessionStorage.getItem(SESSION_KEYS.LAST_FETCH);
      const now = Date.now();

      if (!lastFetch || (now - parseInt(lastFetch)) > STALE_THRESHOLD) {
        console.log('Data is stale, refreshing in background...');
        fetchData(true); // Background refresh - no loading screen
      } else {
        console.log('Data is fresh, no refresh needed');
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfStale();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // On iOS native, visibilitychange doesn't fire reliably on app resume
    let appStateHandle;
    if (Capacitor.isNativePlatform()) {
      appStateHandle = App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          refreshIfStale();
        }
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (appStateHandle) {
        appStateHandle.then(h => h.remove());
      }
    };
  }, [fetchData]);

  // Real-time subscriptions — debounced so rapid-fire DB changes
  // (e.g. multiple ratings) only trigger one background refetch
  useEffect(() => {
    const debouncedRefresh = () => {
      clearTimeout(realtimeDebounceRef.current);
      realtimeDebounceRef.current = setTimeout(() => fetchData(true), 5000);
    };

    const ratingsChannel = supabase
      .channel('ratings-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ratings' }, debouncedRefresh)
      .subscribe();

    const commentsChannel = supabase
      .channel('comments-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'comments' }, debouncedRefresh)
      .subscribe();

    const barsChannel = supabase
      .channel('bars-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bars' }, debouncedRefresh)
      .subscribe();

    const photosChannel = supabase
      .channel('photos-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bar_photos' }, debouncedRefresh)
      .subscribe();

    return () => {
      clearTimeout(realtimeDebounceRef.current);
      supabase.removeChannel(ratingsChannel);
      supabase.removeChannel(commentsChannel);
      supabase.removeChannel(barsChannel);
      supabase.removeChannel(photosChannel);
    };
  }, [fetchData]);

  // Add a rating (updated to include drink_selection)
  const addRating = useCallback(async (ratingData) => {
    if (!user || !profile) throw new Error('Must be logged in to rate');

    const { data, error } = await supabase
      .from('ratings')
      .upsert({
        bar_id: ratingData.barId,
        user_id: user.id,
        overall: ratingData.overall,
        price: ratingData.price || null,
        ambiance: ratingData.ambiance || null,
        crowd: ratingData.crowd || null,
        service: ratingData.service || null,
        drink_selection: ratingData.drinkSelection || null,
        is_dive_bar: ratingData.isDiveBar
      }, {
        onConflict: 'bar_id,user_id'
      })
      .select()
      .single();

    if (error) throw error;

    await logActivity(user.id, 'rating', 'bar', ratingData.barId, {
      overall: ratingData.overall,
      isDiveBar: ratingData.isDiveBar
    });

    // Check and award badges after rating
    try {
      await supabase.rpc('check_and_award_badges', { p_user_id: user.id });
    } catch (badgeError) {
      // Badge function might not exist yet, fail silently
      console.log('Badge check skipped:', badgeError.message);
    }

    // Notify followers that this user rated a bar
    const barName = bars.find(b => b.id === ratingData.barId)?.name || 'a bar';
    for (const followerId of followers) {
      notifyFriendRating(followerId, profile.display_name, barName).catch(console.error);
    }

    await fetchData();
    return data;
  }, [user, profile, logActivity, fetchData, bars, followers]);

  const addCheckIn = useCallback(async (barId) => {
    if (!user || !profile) throw new Error('Must be logged in to check in');

    const userRating = ratings.find(r => r.barId === barId && r.userId === user.id);
    if (!canCheckInAtBar(userRating)) {
      throw new Error('Rate this bar once before checking in');
    }

    const bar = bars.find(b => b.id === barId);
    const phrase = pickCheckInPhrase();
    const messageText = formatCheckInMessage(phrase, bar?.name);

    const { data, error } = await supabase
      .from('check_ins')
      .insert({
        user_id: user.id,
        bar_id: barId,
        message_key: phrase.key,
        message_text: messageText
      })
      .select()
      .single();

    if (error) {
      if (error.code === '42501' || /policy|permission/i.test(error.message || '')) {
        throw new Error('Rate this bar once before checking in');
      }
      if (/does not exist|schema cache|check_ins/i.test(error.message || '')) {
        throw new Error('Check-ins are not enabled on the server yet.');
      }
      throw error;
    }

    const recipientIds = [...new Set([...(followers || []), ...(friends || [])])];
    for (const recipientId of recipientIds) {
      supabase.from('notifications').insert({
        user_id: recipientId,
        type: 'check_in',
        from_user_id: user.id,
        reference_id: data.id,
        data: { bar_id: barId, bar_name: bar?.name, message_text: messageText }
      }).then(() => {}).catch(() => {});
      notifyCheckIn(recipientId, profile.display_name, bar?.name, messageText).catch(console.error);
    }

    await logActivity(user.id, 'check_in', 'bar', barId, { messageKey: phrase.key });
    return data;
  }, [user, profile, ratings, bars, followers, friends, logActivity]);

  // Add a comment/review
  const addComment = useCallback(async (barId, text, visibility = 'public', isReview = false) => {
    if (!user || !profile) throw new Error('Must be logged in to comment');

    // Only validate if text is provided
    if (!text || text.trim().length === 0) {
      throw new Error('Review text is required');
    }

    // Validate text length
    if (text.length < 10) {
      throw new Error('Review must be at least 10 characters');
    }
    if (text.length > 500) {
      throw new Error('Review must be 500 characters or less');
    }

    // Check for URLs (spam prevention)
    const urlPattern = /https?:\/\/|www\./i;
    if (urlPattern.test(text)) {
      throw new Error('URLs are not allowed in reviews');
    }

    // Content filter
    const contentCheck = validateContent(text, { minLength: 10, maxLength: 500, label: 'Review' });
    if (!contentCheck.valid) {
      throw new Error(contentCheck.error);
    }

    console.log('=== INSERTING COMMENT/REVIEW ===');
    console.log('Bar ID:', barId);
    console.log('User ID:', user.id);
    console.log('Text:', text.substring(0, 50) + '...');
    console.log('Visibility:', visibility);
    console.log('Is Review:', isReview);

    const { data, error } = await supabase
      .from('comments')
      .insert({
        bar_id: barId,
        user_id: user.id,
        text,
        visibility: visibility || 'public',
        is_review: isReview
      })
      .select()
      .single();

    console.log('Insert result:', data);
    if (error) {
      console.error('Insert error:', error);
      throw error;
    }
    console.log('=== COMMENT/REVIEW SAVED SUCCESSFULLY ===');

    await logActivity(user.id, isReview ? 'review' : 'comment', 'bar', barId);

    // Notify users who have rated this bar (except the commenter)
    const barName = bars.find(b => b.id === barId)?.name || 'a bar';
    const barRaters = ratings.filter(r => r.barId === barId && r.userId !== user.id);
    const notifiedUserIds = new Set();
    for (const rater of barRaters) {
      if (!notifiedUserIds.has(rater.userId)) {
        notifiedUserIds.add(rater.userId);
        notifyBarComment(rater.userId, profile.display_name, barName).catch(console.error);
      }
    }

    await fetchData();
    return data;
  }, [user, profile, logActivity, fetchData, bars, ratings]);

  // Update a comment/review
  const updateComment = useCallback(async (commentId, text, visibility) => {
    if (!user || !profile) throw new Error('Must be logged in');

    // Validate text length
    if (text.length < 10) {
      throw new Error('Review must be at least 10 characters');
    }
    if (text.length > 500) {
      throw new Error('Review must be 500 characters or less');
    }

    // Check for URLs
    const urlPattern = /https?:\/\/|www\./i;
    if (urlPattern.test(text)) {
      throw new Error('URLs are not allowed in reviews');
    }

    const { data, error } = await supabase
      .from('comments')
      .update({ text, visibility })
      .eq('id', commentId)
      .eq('user_id', user.id) // Only allow updating own comments
      .select()
      .single();

    if (error) throw error;

    await fetchData();
    return data;
  }, [user, profile, fetchData]);

  // Delete a comment
  const deleteComment = useCallback(async (commentId) => {
    if (!user || !profile) throw new Error('Must be logged in');

    const { error } = await supabase
      .from('comments')
      .delete()
      .eq('id', commentId)
      .eq('user_id', user.id); // Only allow deleting own comments

    if (error) throw error;

    await fetchData();
  }, [user, profile, fetchData]);

  // Get user's review for a bar
  const getUserReview = useCallback((barId) => {
    if (!user) return null;
    return comments.find(c => c.barId === barId && c.userId === user.id && c.isReview);
  }, [user, comments]);

  // Check for duplicate bar names
  const checkDuplicateBar = useCallback(async (barName) => {
    const normalizedName = barName.toLowerCase().trim();
    
    // Check existing bars
    const { data: existingBars } = await supabase
      .from('bars')
      .select('name')
      .ilike('name', `%${normalizedName}%`);
    
    // Check pending bar requests
    const { data: pendingRequests } = await supabase
      .from('bar_requests')
      .select('name')
      .eq('status', 'pending')
      .ilike('name', `%${normalizedName}%`);
    
    const duplicates = {
      existingBar: null,
      pendingRequest: null
    };
    
    // Find close matches in existing bars
    if (existingBars && existingBars.length > 0) {
      const exactMatch = existingBars.find(b => 
        b.name.toLowerCase().trim() === normalizedName
      );
      const similarMatch = existingBars.find(b => 
        b.name.toLowerCase().includes(normalizedName) || 
        normalizedName.includes(b.name.toLowerCase())
      );
      duplicates.existingBar = exactMatch || similarMatch;
    }
    
    // Find close matches in pending requests
    if (pendingRequests && pendingRequests.length > 0) {
      const exactMatch = pendingRequests.find(r => 
        r.name.toLowerCase().trim() === normalizedName
      );
      const similarMatch = pendingRequests.find(r => 
        r.name.toLowerCase().includes(normalizedName) || 
        normalizedName.includes(r.name.toLowerCase())
      );
      duplicates.pendingRequest = exactMatch || similarMatch;
    }
    
    return duplicates;
  }, []);

  // Geocode an address using OpenStreetMap Nominatim (free, no API key needed)
  const geocodeAddress = async (address, metro, { broaderMetro = false } = {}) => {
    try {
      const suffix = metro ? `, ${metro.name}, ${metro.state}` : ', Chicago, IL';
      const alreadyIncludesCity = metro
        ? address.toLowerCase().includes(metro.name.toLowerCase())
        : address.includes('Chicago');
      const searchAddress = alreadyIncludesCity
        ? address
        : broaderMetro && metro
          ? `${address}, ${metro.state}`
          : `${address}${suffix}`;
      const encodedAddress = encodeURIComponent(searchAddress);
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodedAddress}&limit=1`,
        {
          headers: {
            'User-Agent': 'Tilted-App/1.0 (https://gettilted.app)'
          }
        }
      );
      
      if (!response.ok) {
        console.log('Geocoding API error:', response.status);
        return null;
      }
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
          city: municipalityFromNominatim(data[0].address),
          state: data[0].address?.state || metro?.state || '',
        };
      }
      
      return null;
    } catch (err) {
      console.log('Geocoding failed:', err.message);
      return null;
    }
  };

  // Submit bar request with auto-approval
  const submitBarRequest = useCallback(async (barData, options = {}) => {
    if (!user || !profile) throw new Error('Must be logged in to submit a bar');
    
    // Get recent submission count for rate limiting (graceful fallback if function doesn't exist)
    let recentSubmissionCount = 0;
    try {
      const { data: countData, error: countError } = await supabase.rpc('get_recent_submission_count', {
        p_user_id: user.id,
        p_minutes: 10
      });
      if (!countError && countData !== null) {
        recentSubmissionCount = countData;
      }
    } catch (err) {
      console.log('Rate limiting check skipped (function may not exist yet)');
    }
    
    // Geocode the address to get lat/lng coordinates
    let coordinates = options.coordinates;
    const metro = getMetroById(barData.metroId) || getMetroById('chicago');
    const broaderMetro = isBroaderMetroNeighborhood(barData.neighborhood);

    if (!coordinates && barData.address) {
      console.log('Geocoding address:', barData.address);
      coordinates = await geocodeAddress(barData.address, metro, { broaderMetro });
      console.log('Geocoding result:', coordinates);
    }
    
    // If geocoding failed, show error - do NOT use fallback coordinates
    if (!coordinates) {
      throw new Error('Could not find that address. Please check the address and try again.');
    }

    let cityName = metro.name;
    if (broaderMetro) {
      const core = getBroaderMetroCoreName(barData.neighborhood);
      cityName = coordinates.city || parseTownFromAddress(barData.address) || '';
      if (!cityName || (core && cityName.toLowerCase() === core.toLowerCase())) {
        cityName = await reverseGeocodeTown(coordinates.lat, coordinates.lng) || cityName;
      }
      if (!cityName || (core && cityName.toLowerCase() === core.toLowerCase())) {
        cityName = parseTownFromAddress(barData.address) || metro.name;
      }
    }
    
    // Validate the submission against the selected metro
    const validation = validateBarSubmission(barData, {
      coordinates,
      recentSubmissionCount,
      metro
    });
    
    if (!validation.approved) {
      throw new Error(validation.reason);
    }
    
    // Try to use the auto_approve_bar RPC function first
    let barId = null;
    let useDirectInsert = false;
    
    try {
      const { data: rpcBarId, error: approveError } = await supabase.rpc('auto_approve_bar', {
        p_name: barData.name.trim(),
        p_address: barData.address?.trim() || '',
        p_neighborhood: barData.neighborhood,
        p_description: barData.description?.trim() || '',
        p_attributes: barData.attributes || {},
        p_submitted_by: user.id,
        p_lat: coordinates.lat,
        p_lng: coordinates.lng
      });
      
      if (approveError) {
        console.log('RPC function error:', approveError.message, approveError.code);
        useDirectInsert = true;
      } else {
        barId = rpcBarId;
      }
    } catch (err) {
      console.log('RPC call failed:', err.message);
      useDirectInsert = true;
    }
    
    // Fallback: Direct insert into bars table if RPC function doesn't exist
    if (useDirectInsert) {
      console.log('Attempting direct insert into bars table...');
      const barInsert = {
          name: barData.name.trim(),
          address: barData.address?.trim() || '',
          neighborhood: barData.neighborhood,
          description: barData.description?.trim() || '',
          attributes: barData.attributes || {},
          lat: coordinates.lat,
          lng: coordinates.lng,
          metro_id: metro.id,
          city: cityName,
          state: metro.state
        };
      let { data: insertedBar, error: insertError } = await supabase
        .from('bars')
        .insert(barInsert)
        .select('id')
        .single();

      if (insertError && /metro_id|city|state/i.test(insertError.message || '')) {
        delete barInsert.metro_id;
        delete barInsert.city;
        delete barInsert.state;
        ({ data: insertedBar, error: insertError } = await supabase
          .from('bars')
          .insert(barInsert)
          .select('id')
          .single());
      }
      
      if (insertError) {
        console.error('Direct insert error:', insertError.message, insertError.code);
        
        // User-friendly error messages (don't show technical details)
        if (insertError.code === '42501' || insertError.message?.includes('permission') || insertError.message?.includes('policy')) {
          throw new Error('Unable to add bar right now. Please try again in a moment.');
        } else if (insertError.code === '23505' || insertError.message?.includes('duplicate')) {
          throw new Error('A bar with this name may already exist. Please check the bar list.');
        } else {
          throw new Error('Something went wrong. Please try again.');
        }
      }
      
      barId = insertedBar.id;
    }

    if (barId && broaderMetro && cityName) {
      try {
        await supabase
          .from('bars')
          .update({ city: cityName, state: metro.state, metro_id: metro.id })
          .eq('id', barId);
      } catch { /* column or policy may block */ }
    }
    
    await logActivity(user.id, 'bar_approved', 'bar', barId);
    
    // Refresh bars data
    await fetchData(true);
    
    // Return success with the new bar ID and bar name
    return { 
      id: barId, 
      name: barData.name.trim(),
      approved: true, 
      message: "Bar added! It's now live on Tilted." 
    };
  }, [user, profile, logActivity, fetchData]);

  // ============================================
  // FRIEND REQUEST FUNCTIONS
  // ============================================

  // Send friend request
  const sendFriendRequest = useCallback(async (toUserId) => {
    if (!user) throw new Error('Must be logged in');

    // Check if already friends
    if (friends.includes(toUserId)) {
      throw new Error('Already friends with this user');
    }

    // Check if request already exists
    const existingOutgoing = friendRequests.outgoing.find(r => r.to_user_id === toUserId);
    if (existingOutgoing) {
      throw new Error('Friend request already sent');
    }

    // Check if they already sent us a request (auto-accept)
    const existingIncoming = friendRequests.incoming.find(r => r.from_user_id === toUserId);
    if (existingIncoming) {
      // They already sent us a request, accept it instead
      await acceptFriendRequest(existingIncoming.id);
      return;
    }

    const { data, error } = await supabase
      .from('friend_requests')
      .insert({
        from_user_id: user.id,
        to_user_id: toUserId
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('Friend request already sent');
      }
      throw error;
    }

    // Create notification for recipient
    await supabase.from('notifications').insert({
      user_id: toUserId,
      type: 'friend_request',
      from_user_id: user.id,
      reference_id: data.id
    });

    // Send push notification
    notifyFriendRequest(toUserId, profile.display_name).catch(console.error);

    await logActivity(user.id, 'friend_request_sent', 'user', toUserId);
    await fetchUserData();
  }, [user, friends, friendRequests, logActivity, fetchUserData]);

  // Accept friend request
  const acceptFriendRequest = useCallback(async (requestId) => {
    if (!user) throw new Error('Must be logged in');

    // Use the database function to handle mutual friendship creation
    const { error } = await supabase.rpc('accept_friend_request', { request_id: requestId });

    if (error) throw error;

    await logActivity(user.id, 'friend_request_accepted', 'friend_request', requestId);
    await fetchUserData();
  }, [user, logActivity, fetchUserData]);

  // Decline friend request
  const declineFriendRequest = useCallback(async (requestId) => {
    if (!user) throw new Error('Must be logged in');

    const { error } = await supabase
      .from('friend_requests')
      .update({ status: 'declined', responded_at: new Date().toISOString() })
      .eq('id', requestId)
      .eq('to_user_id', user.id);

    if (error) throw error;

    await fetchUserData();
  }, [user, fetchUserData]);

  // Cancel sent friend request
  const cancelFriendRequest = useCallback(async (requestId) => {
    if (!user) throw new Error('Must be logged in');

    const { error } = await supabase
      .from('friend_requests')
      .delete()
      .eq('id', requestId)
      .eq('from_user_id', user.id);

    if (error) throw error;

    await fetchUserData();
  }, [user, fetchUserData]);

  // Remove friend (unfriend)
  const removeFriend = useCallback(async (friendId) => {
    if (!user) throw new Error('Must be logged in');

    // Delete both directions of the friendship
    await supabase
      .from('friendships')
      .delete()
      .eq('user_id', user.id)
      .eq('friend_id', friendId);

    await supabase
      .from('friendships')
      .delete()
      .eq('user_id', friendId)
      .eq('friend_id', user.id);

    await logActivity(user.id, 'unfriend', 'user', friendId);
    setFriends(prev => prev.filter(id => id !== friendId));
  }, [user, logActivity]);

  // Check friend status with another user (legacy)
  const getFriendStatus = useCallback((userId) => {
    if (!user) return 'none';
    if (friends.includes(userId)) return 'friends';
    if (friendRequests.outgoing.find(r => r.to_user_id === userId)) return 'pending_sent';
    if (friendRequests.incoming.find(r => r.from_user_id === userId)) return 'pending_received';
    return 'none';
  }, [user, friends, friendRequests]);

  // ============================================
  // FOLLOW SYSTEM FUNCTIONS (New)
  // ============================================

  // Check follow status with another user
  const getFollowStatus = useCallback((userId) => {
    if (!user) return 'not_following';
    if (following.includes(userId)) return 'following';
    if (followRequests.outgoing.find(r => r.following_id === userId)) return 'pending';
    return 'not_following';
  }, [user, following, followRequests]);

  // Follow a user
  const followUser = useCallback(async (targetUserId) => {
    if (!user) throw new Error('Must be logged in');

    // Check if already following
    if (following.includes(targetUserId)) {
      throw new Error('Already following this user');
    }

    try {
      // Use the database function which handles privacy logic
      const { data, error } = await supabase.rpc('follow_user', {
        p_following_id: targetUserId
      });

      if (error) throw error;

      // Update local state based on result
      if (data && data[0]?.status === 'active') {
        setFollowing(prev => [...prev, targetUserId]);
        setFriends(prev => [...new Set([...prev, targetUserId])]); // Backward compat
      } else if (data && data[0]?.status === 'pending') {
        setFollowRequests(prev => ({
          ...prev,
          outgoing: [...prev.outgoing, { following_id: targetUserId }]
        }));
      }

      await logActivity(user.id, 'follow', 'user', targetUserId);
      trackEvent('user_followed', { followed_user_id: targetUserId });

      // Send push notification to the user being followed
      if (data && data[0]?.status === 'active') {
        notifyNewFollower(targetUserId, profile.display_name).catch(console.error);
      }

      // Refresh user data to ensure consistency
      await fetchUserData();

      return data;
    } catch (err) {
      console.error('Error following user:', err);
      throw err;
    }
  }, [user, following, logActivity, fetchUserData]);

  // Unfollow a user
  const unfollowUser = useCallback(async (targetUserId) => {
    if (!user) throw new Error('Must be logged in');

    try {
      const { error } = await supabase.rpc('unfollow_user', {
        p_following_id: targetUserId
      });

      if (error) throw error;

      // Update local state immediately for responsiveness
      setFollowing(prev => prev.filter(id => id !== targetUserId));
      setFriends(prev => prev.filter(id => id !== targetUserId)); // Backward compat

      await logActivity(user.id, 'unfollow', 'user', targetUserId);

      // Refresh user data to ensure consistency
      await fetchUserData();
    } catch (err) {
      console.error('Error unfollowing user:', err);
      throw err;
    }
  }, [user, logActivity, fetchUserData]);

  // Accept a follow request (for private accounts)
  const acceptFollowRequest = useCallback(async (followerUserId) => {
    if (!user) throw new Error('Must be logged in');

    try {
      const { error } = await supabase.rpc('accept_follow_request', {
        p_follower_id: followerUserId
      });

      if (error) throw error;

      // Update local state
      setFollowers(prev => [...prev, followerUserId]);
      setFollowRequests(prev => ({
        ...prev,
        incoming: prev.incoming.filter(r => r.follower_id !== followerUserId)
      }));

      await logActivity(user.id, 'accept_follow_request', 'user', followerUserId);

      // Refresh user data to ensure consistency
      await fetchUserData();
    } catch (err) {
      console.error('Error accepting follow request:', err);
      throw err;
    }
  }, [user, logActivity, fetchUserData]);

  // Deny a follow request
  const denyFollowRequest = useCallback(async (followerUserId) => {
    if (!user) throw new Error('Must be logged in');

    try {
      const { error } = await supabase.rpc('deny_follow_request', {
        p_follower_id: followerUserId
      });

      if (error) throw error;

      // Update local state
      setFollowRequests(prev => ({
        ...prev,
        incoming: prev.incoming.filter(r => r.follower_id !== followerUserId)
      }));
      
      // Refresh user data to ensure consistency
      await fetchUserData();
    } catch (err) {
      console.error('Error denying follow request:', err);
      throw err;
    }
  }, [user, fetchUserData]);

  // Cancel an outgoing follow request
  const cancelFollowRequest = useCallback(async (targetUserId) => {
    if (!user) throw new Error('Must be logged in');

    try {
      const { error } = await supabase.rpc('unfollow_user', {
        p_following_id: targetUserId
      });

      if (error) throw error;

      // Update local state
      setFollowRequests(prev => ({
        ...prev,
        outgoing: prev.outgoing.filter(r => r.following_id !== targetUserId)
      }));
    } catch (err) {
      console.error('Error canceling follow request:', err);
      throw err;
    }
  }, [user]);

  // Check if current user can view another user's content (privacy check)
  const canViewUserContent = useCallback(async (targetUserId) => {
    if (!user) return false;
    if (user.id === targetUserId) return true;

    try {
      const { data, error } = await supabase.rpc('can_view_user_content', {
        p_target_user_id: targetUserId
      });

      if (error) throw error;
      return data === true;
    } catch (err) {
      console.error('Error checking content visibility:', err);
      return false;
    }
  }, [user]);

  // ============================================
  // BLOCK USER FUNCTIONS
  // ============================================

  const [blockedUsers, setBlockedUsers] = useState([]);

  useEffect(() => {
    if (user) {
      supabase
        .from('blocked_users')
        .select('blocked_id')
        .eq('blocker_id', user.id)
        .then(({ data }) => {
          setBlockedUsers(data?.map(b => b.blocked_id) || []);
        });
    } else {
      setBlockedUsers([]);
    }
  }, [user]);

  const blockUser = useCallback(async (targetUserId) => {
    if (!user) throw new Error('Must be logged in');
    if (targetUserId === user.id) throw new Error('Cannot block yourself');

    const { error } = await supabase
      .from('blocked_users')
      .insert({ blocker_id: user.id, blocked_id: targetUserId });

    if (error && !error.message?.includes('duplicate')) throw error;

    setBlockedUsers(prev => [...prev, targetUserId]);

    // Also unfollow/unfriend the blocked user
    try {
      await supabase.from('follows').delete().match({
        follower_id: user.id, following_id: targetUserId
      });
      await supabase.from('follows').delete().match({
        follower_id: targetUserId, following_id: user.id
      });
    } catch (_) {}

    await logActivity(user.id, 'block_user', 'user', targetUserId);
  }, [user, logActivity]);

  const unblockUser = useCallback(async (targetUserId) => {
    if (!user) throw new Error('Must be logged in');

    const { error } = await supabase
      .from('blocked_users')
      .delete()
      .match({ blocker_id: user.id, blocked_id: targetUserId });

    if (error) throw error;

    setBlockedUsers(prev => prev.filter(id => id !== targetUserId));
  }, [user]);

  const isUserBlocked = useCallback((targetUserId) => {
    return blockedUsers.includes(targetUserId);
  }, [blockedUsers]);

  // ============================================
  // FRIEND FEED FUNCTIONS
  // ============================================

  // Get friend feed (ratings from friends)
  const getFriendFeed = useCallback(async (limit = 20, offset = 0) => {
    if (!user) return [];

    try {
      const { data, error } = await supabase.rpc('get_friend_feed', {
        user_uuid: user.id,
        limit_count: limit,
        offset_count: offset
      });

      if (error) throw error;

      const friendIds = [...new Set([...(friends || []), ...(following || [])])];
      let checkInItems = [];
      if (friendIds.length > 0) {
        const { data: checkIns, error: checkInError } = await supabase
          .from('check_ins')
          .select('id, user_id, bar_id, message_key, message_text, created_at')
          .in('user_id', friendIds)
          .order('created_at', { ascending: false })
          .limit(limit);

        if (!checkInError && checkIns?.length) {
          checkInItems = checkIns.map((checkIn) => {
            const bar = bars.find(b => b.id === checkIn.bar_id);
            const checkInUser = users.find(u => u.id === checkIn.user_id);
            return {
              type: 'check_in',
              check_in_id: checkIn.id,
              rating_id: `checkin-${checkIn.id}`,
              bar_id: checkIn.bar_id,
              bar_name: bar?.name || 'a bar',
              bar_neighborhood: bar?.neighborhood || '',
              user_id: checkIn.user_id,
              username: checkInUser?.username || 'user',
              display_name: checkInUser?.displayName || 'Unknown',
              avatar_url: checkInUser?.avatarUrl || null,
              message_text: checkIn.message_text,
              created_at: checkIn.created_at,
              comment_count: 0
            };
          });
        }
      }

      const ratingItems = (data || []).map((item) => ({ ...item, type: 'rating' }));
      return [...ratingItems, ...checkInItems]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, limit);
    } catch (err) {
      console.error('Error fetching friend feed:', err);
      // Fallback to manual query if RPC not available
      const friendRatings = ratings
        .filter(r => friends.includes(r.userId))
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(offset, offset + limit);
      
      return friendRatings.map(r => {
        const bar = bars.find(b => b.id === r.barId);
        const ratingUser = users.find(u => u.id === r.userId);
        return {
          rating_id: r.id,
          bar_id: r.barId,
          bar_name: bar?.name || 'Unknown',
          bar_neighborhood: bar?.neighborhood || '',
          user_id: r.userId,
          username: ratingUser?.username || 'user',
          display_name: ratingUser?.displayName || 'Unknown',
          avatar_url: null,
          overall: r.overall,
          is_dive_bar: r.isDiveBar,
          review_text: null,
          created_at: new Date(r.createdAt).toISOString(),
          comment_count: 0
        };
      });
    }
  }, [user, friends, following, ratings, bars, users]);

  // Get comments on a friend's rating
  const getFriendRatingComments = useCallback(async (ratingId) => {
    const { data, error } = await supabase
      .from('friend_rating_comments')
      .select(`
        *,
        profiles:user_id (id, username, display_name, avatar_url)
      `)
      .eq('rating_id', ratingId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }, []);

  // Add comment on a friend's rating
  const addFriendRatingComment = useCallback(async (ratingId, text) => {
    if (!user) throw new Error('Must be logged in');

    const contentCheck = validateContent(text, { minLength: 1, maxLength: 500, label: 'Comment' });
    if (!contentCheck.valid) {
      throw new Error(contentCheck.error);
    }

    const { data, error } = await supabase
      .from('friend_rating_comments')
      .insert({
        rating_id: ratingId,
        user_id: user.id,
        text
      })
      .select()
      .single();

    if (error) throw error;

    // Get the rating to find the author
    const rating = ratings.find(r => r.id === ratingId);
    if (rating && rating.userId !== user.id) {
      // Create notification for rating author
      await supabase.from('notifications').insert({
        user_id: rating.userId,
        type: 'rating_comment',
        from_user_id: user.id,
        reference_id: ratingId
      });

      // Send push notification
      notifyRatingComment(rating.userId, profile.display_name).catch(console.error);
    }

    await logActivity(user.id, 'friend_rating_comment', 'rating', ratingId);
    return data;
  }, [user, ratings, logActivity]);

  // ============================================
  // NOTIFICATION FUNCTIONS
  // ============================================

  // Mark notification as read
  const markNotificationRead = useCallback(async (notificationId) => {
    if (!user) return;

    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId)
      .eq('user_id', user.id);

    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
  }, [user]);

  // Mark all notifications as read
  const markAllNotificationsRead = useCallback(async () => {
    if (!user) return;

    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', user.id)
      .eq('is_read', false);

    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  }, [user]);

  // Track a share action for badge progress
  const trackShare = useCallback(async (barId, shareMethod) => {
    if (!user) return;

    try {
      // Increment share count via RPC (also checks badges)
      const { data, error } = await supabase.rpc('increment_share_count', { 
        p_user_id: user.id 
      });

      if (error) {
        console.error('Error tracking share:', error);
        return;
      }

      // Log the activity
      await logActivity(user.id, 'share', 'bar', barId, { method: shareMethod });

      return data; // Returns new share count
    } catch (err) {
      console.error('Error tracking share:', err);
    }
  }, [user, logActivity]);

  // Get unread notification count
  const unreadNotificationCount = notifications.filter(n => !n.is_read).length;

  // Legacy function for backward compatibility
  const addFriend = sendFriendRequest;

  // ============================================
  // LIST FUNCTIONS
  // ============================================

  // Create a new list
  const createList = useCallback(async (title, description = '', privacy = 'private') => {
    if (!user) throw new Error('Must be logged in');

    const { data, error } = await supabase
      .from('lists')
      .insert({
        user_id: user.id,
        title,
        description,
        privacy,
        is_system: false
      })
      .select()
      .single();

    if (error) throw error;

    await fetchUserData();
    return data;
  }, [user, fetchUserData]);

  // Update a list
  const updateList = useCallback(async (listId, updates) => {
    if (!user) throw new Error('Must be logged in');

    const { error } = await supabase
      .from('lists')
      .update({
        title: updates.title,
        description: updates.description,
        privacy: updates.privacy,
        updated_at: new Date().toISOString()
      })
      .eq('id', listId)
      .eq('user_id', user.id);

    if (error) throw error;

    await fetchUserData();
  }, [user, fetchUserData]);

  // Delete a list
  const deleteList = useCallback(async (listId) => {
    if (!user) throw new Error('Must be logged in');

    const { error } = await supabase
      .from('lists')
      .delete()
      .eq('id', listId)
      .eq('user_id', user.id)
      .eq('is_system', false);

    if (error) throw error;

    await fetchUserData();
  }, [user, fetchUserData]);

  // Check if bar is already in a list
  const isBarInList = useCallback((listId, barId) => {
    const list = lists.find(l => l.id === listId);
    if (!list || !list.list_bars) return false;
    return list.list_bars.some(lb => lb.bar_id === barId);
  }, [lists]);

  // Add bar to list
  const addBarToList = useCallback(async (listId, barId) => {
    if (!user) throw new Error('Must be logged in');

    // Check if bar is already in the list
    if (isBarInList(listId, barId)) {
      throw new Error('This bar is already in the list');
    }

    // Get current max position
    const { data: existingBars } = await supabase
      .from('list_bars')
      .select('position')
      .eq('list_id', listId)
      .order('position', { ascending: false })
      .limit(1);

    const newPosition = existingBars && existingBars.length > 0 ? existingBars[0].position + 1 : 0;

    const { data, error } = await supabase
      .from('list_bars')
      .insert({
        list_id: listId,
        bar_id: barId,
        position: newPosition
      })
      .select()
      .single();

    if (error) {
      // Handle duplicate key error gracefully
      if (error.code === '23505') {
        throw new Error('This bar is already in the list');
      }
      throw error;
    }

    // Immediately update local state
    setLists(prevLists => prevLists.map(list => {
      if (list.id === listId) {
        return {
          ...list,
          list_bars: [...(list.list_bars || []), data]
        };
      }
      return list;
    }));
  }, [user, isBarInList]);

  // Remove bar from list
  const removeBarFromList = useCallback(async (listId, barId) => {
    if (!user) throw new Error('Must be logged in');

    const { error } = await supabase
      .from('list_bars')
      .delete()
      .eq('list_id', listId)
      .eq('bar_id', barId);

    if (error) throw error;

    // Immediately update local state
    setLists(prevLists => prevLists.map(list => {
      if (list.id === listId) {
        return {
          ...list,
          list_bars: (list.list_bars || []).filter(lb => lb.bar_id !== barId)
        };
      }
      return list;
    }));
  }, [user]);

  // Get "Want to Try" list
  const getWantToTryList = useCallback(() => {
    return lists.find(l => l.is_system && l.title === 'Want to Try');
  }, [lists]);

  // Check if bar is in Want to Try list
  const isInWantToTry = useCallback((barId) => {
    const wantToTryList = getWantToTryList();
    if (!wantToTryList) return false;
    return wantToTryList.list_bars?.some(lb => lb.bar_id === barId) || false;
  }, [getWantToTryList]);

  // Toggle bar in Want to Try list
  const toggleWantToTry = useCallback(async (barId) => {
    const wantToTryList = getWantToTryList();
    if (!wantToTryList) return;

    if (isInWantToTry(barId)) {
      await removeBarFromList(wantToTryList.id, barId);
    } else {
      await addBarToList(wantToTryList.id, barId);
    }
  }, [getWantToTryList, isInWantToTry, addBarToList, removeBarFromList]);

  // Get a user's lists visible to the current user
  const getUserVisibleLists = useCallback(async (targetUserId) => {
    if (!targetUserId) return [];
    
    // Check if the target user is a friend
    const isFriend = friends.includes(targetUserId);
    
    // Determine which privacy levels to fetch
    // - Always show public lists
    // - Show friends-only lists if they are friends
    // - Show all lists if it's the current user
    let privacyFilter = ['public'];
    if (user && targetUserId === user.id) {
      privacyFilter = ['public', 'friends', 'private'];
    } else if (isFriend) {
      privacyFilter = ['public', 'friends'];
    }

    const { data, error } = await supabase
      .from('lists')
      .select(`
        *,
        list_bars(
          id,
          bar_id,
          position,
          added_at
        ),
        profiles:user_id (id, username, display_name, avatar_url)
      `)
      .eq('user_id', targetUserId)
      .in('privacy', privacyFilter)
      .eq('is_system', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user lists:', error);
      return [];
    }

    return data || [];
  }, [user, friends]);

  // ============================================
  // PHOTO FUNCTIONS
  // ============================================

  // Upload a photo
  const uploadPhoto = useCallback(async (barId, file, caption = '', visibility = 'public', ratingId = null) => {
    if (!user) throw new Error('Must be logged in');

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      throw new Error(`"${file.name}" is ${fileSizeMB}MB. Max size is 5MB.`);
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error(`"${file.name}" is not a supported format. Use JPG, PNG, or WebP.`);
    }

    // Create unique file path
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${barId}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;

    // Convert File to Blob for iOS WKWebView compatibility
    const uploadBlob = await prepareFileForUpload(file);

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('bar-photos')
      .upload(fileName, uploadBlob);

    if (uploadError) throw uploadError;

    // Create database record with visibility and optional rating_id
    const insertData = {
      bar_id: barId,
      user_id: user.id,
      storage_path: fileName,
      caption,
      visibility: visibility || 'public'
    };
    
    // Only add rating_id if provided
    if (ratingId) {
      insertData.rating_id = ratingId;
    }

    const { data, error: dbError } = await supabase
      .from('bar_photos')
      .insert(insertData)
      .select()
      .single();

    if (dbError) {
      // Clean up uploaded file if db insert fails
      await supabase.storage.from('bar-photos').remove([fileName]);
      throw dbError;
    }

    await logActivity(user.id, 'photo_upload', 'bar', barId);
    await fetchData();
    return data;
  }, [user, logActivity, fetchData]);

  // Delete a photo
  const deletePhoto = useCallback(async (photoId, storagePath) => {
    if (!user) throw new Error('Must be logged in');

    // Delete from storage
    await supabase.storage.from('bar-photos').remove([storagePath]);

    // Delete from database
    const { error } = await supabase
      .from('bar_photos')
      .delete()
      .eq('id', photoId);

    if (error) throw error;

    await fetchData();
  }, [user, fetchData]);

  // Get photos for a bar
  const getBarPhotos = useCallback((barId) => {
    return photos.filter(p => p.barId === barId);
  }, [photos]);

  // Get most recent photo for a bar (for thumbnails)
  const getBarThumbnail = useCallback((barId) => {
    const barPhotos = getBarPhotos(barId);
    return barPhotos.length > 0 ? barPhotos[0] : null;
  }, [getBarPhotos]);

  // Calculate bar stats (updated to include drink selection)
  const getBarStats = useCallback((barId) => {
    const barRatings = ratings.filter(r => r.barId === barId);

    if (barRatings.length === 0) {
      return {
        overall: 0,
        price: 0,
        ambiance: 0,
        crowd: 0,
        service: 0,
        drinkSelection: 0,
        divePercentage: 0,
        totalRatings: 0
      };
    }

    const avg = (arr, key) => {
      const values = arr.filter(r => r[key] != null);
      return values.length > 0 ? values.reduce((sum, r) => sum + r[key], 0) / values.length : 0;
    };
    const diveVotes = barRatings.filter(r => r.isDiveBar).length;

    return {
      overall: avg(barRatings, 'overall'),
      price: avg(barRatings, 'price'),
      ambiance: avg(barRatings, 'ambiance'),
      crowd: avg(barRatings, 'crowd'),
      service: avg(barRatings, 'service'),
      drinkSelection: avg(barRatings, 'drinkSelection'),
      divePercentage: (diveVotes / barRatings.length) * 100,
      totalRatings: barRatings.length
    };
  }, [ratings]);

  // Get bars that qualify as dive bars (>50% vote)
  const getDiveBars = useCallback(() => {
    return bars.filter(bar => {
      const stats = getBarStats(bar.id);
      return stats.divePercentage > 50;
    });
  }, [bars, getBarStats]);

  // Get friend ratings
  const getFriendRatings = useCallback(() => {
    return ratings.filter(r => friends.includes(r.userId));
  }, [ratings, friends]);

  // Get user's rating for a bar
  const getUserRating = useCallback((barId) => {
    if (!user) return null;
    return ratings.find(r => r.barId === barId && r.userId === user.id);
  }, [user, ratings]);

  // Clear session data (call on logout to ensure next login shows loading)
  const clearSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEYS.INITIAL_LOAD_COMPLETE);
    sessionStorage.removeItem(SESSION_KEYS.LAST_FETCH);
    sessionStorage.removeItem(SESSION_KEYS.FOLLOWING);
    sessionStorage.removeItem(SESSION_KEYS.FOLLOWERS);
    isInitialLoadRef.current = true;
    lastFetchTimeRef.current = null;
  }, []);

  const value = {
    bars,
    ratings,
    comments,
    users,
    friends,
    lists,
    photos,
    loading,
    isRefreshing, // For showing subtle refresh indicator
    error,
    clearSession, // Call on logout to reset initial load state
    currentUser: profile ? {
      id: user?.id,
      username: profile.username,
      displayName: profile.display_name,
      isAdmin: profile.is_admin
    } : null,
    // Rating & comment functions
    addRating,
    addCheckIn,
    addComment,
    updateComment,
    deleteComment,
    getUserReview,
    submitBarRequest,
    checkDuplicateBar,
    // Friend functions (legacy)
    friends,
    friendRequests,
    notifications,
    unreadNotificationCount,
    sendFriendRequest,
    acceptFriendRequest: acceptFriendRequest, // Legacy friend request accept
    declineFriendRequest,
    cancelFriendRequest: cancelFriendRequest, // Legacy friend request cancel
    addFriend, // Legacy alias for sendFriendRequest
    removeFriend,
    getFriendStatus,
    getFriendFeed,
    blockUser,
    unblockUser,
    isUserBlocked,
    blockedUsers,
    getFriendRatingComments,
    addFriendRatingComment,
    markNotificationRead,
    markAllNotificationsRead,
    trackShare,
    // Follow functions (new system)
    following,
    followers,
    followRequests,
    followUser,
    unfollowUser,
    acceptFollowRequest,
    denyFollowRequest,
    cancelFollowRequest,
    getFollowStatus,
    canViewUserContent,
    // List functions
    createList,
    updateList,
    deleteList,
    addBarToList,
    removeBarFromList,
    isBarInList,
    getWantToTryList,
    isInWantToTry,
    toggleWantToTry,
    friendLists,
    getUserVisibleLists,
    // Photo functions
    uploadPhoto,
    deletePhoto,
    getBarPhotos,
    getBarThumbnail,
    // Stats functions
    getBarStats,
    getDiveBars,
    getFriendRatings,
    getUserRating,
    refreshData: fetchData,
    refreshUserData: fetchUserData
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
